import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { finalize } from "rxjs/operators";
import { Turbine } from "../interfaces/turbines/turbine.interface";
import { TurbineService } from "../services/turbine.service";
import { TurbineBulkUploadComponent } from "./bulk-upload/turbine-bulk-upload.component";
import {
  GridColumn,
  GridAction,
  GridConfig,
} from "../shared/data-grid/data-grid.component";

@Component({
    selector: "app-turbine-list",
    templateUrl: "./turbine-list.component.html",
    styleUrls: ["./turbine-list.component.css"],
    standalone: false
})
export class TurbineListComponent implements OnInit {
  // Data grid properties
  data: Turbine[] = [];
  columns: GridColumn[] = [];
  actions: GridAction[] = [];
  config: GridConfig = {};
  loading = false;
  error: string | null = null;

  // Status filter properties
  showActiveOnly = true;
  showInactiveOnly = false;
  
  constructor(
    private turbineService: TurbineService, 
    private router: Router,
    private dialog: MatDialog
  ) {
    this.setupGridConfiguration();
  }
  private setupGridConfiguration(): void {
    // Configure columns - ordered to match the template structure
    this.columns = [
      {
        key: "businessUnit",
        label: "Business Unit",
        sortable: true,
        filterable: true,
        accessor: (item: Turbine) => this.getBusinessUnitName(item),
      },
      {
        key: "assetGroup",
        label: "Asset Group",
        sortable: true,
        filterable: true,
        accessor: (item: Turbine) => this.getAssetGroupName(item),
      },
      {
        key: "plant",
        label: "Plant",
        sortable: true,
        filterable: true,
        accessor: (item: Turbine) => item.plant?.name || "N/A",
      },
      {
        key: "serialNumber",
        label: "Serial#",
        sortable: true,
        filterable: true,
      },
      {
        key: "unitShortName",
        label: "Short Name",
        sortable: true,
        filterable: true,
      },
      {
        key: "chevronIdentifier",
        label: "CVX Name",
        sortable: true,
        filterable: true,
      },
      {
        key: "country",
        label: "Country",
        sortable: true,
        filterable: true,
      },
      {
        key: "gtOem",
        label: "GT OEM",
        sortable: true,
        filterable: true,
      },
    ];

    // Configure actions
    this.actions = [
      {
        label: "Edit",
        icon: "edit",
        color: "primary",
      },
    ];

    // Configure grid settings
    this.config = {
      showSearch: true,
      showColumnFilters: true,
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100],
      enableSorting: true,
      stickyHeader: true,
    };
  }

  ngOnInit(): void {
    this.loadTurbines();
  }

  loadTurbines(): void {
    this.loading = true;
    this.error = null;

    // Prepare status filter parameters
    const params: any = {};
    if (this.showActiveOnly && !this.showInactiveOnly) {
      params.status = "active";
    } else if (!this.showActiveOnly && this.showInactiveOnly) {
      params.status = "inactive";
    } else if (!this.showActiveOnly && !this.showInactiveOnly) {
      // Show no turbines if both are unchecked
      this.data = [];
      this.loading = false;
      return;
    } else {
      params.status = "all"; // Show all turbines if both are checked
    }

    this.turbineService
      .searchTurbines(params)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (turbines) => {
          this.data = turbines;
        },
        error: (error) => {
          this.error = "Failed to load turbines. Please try again.";
          console.error("Error loading turbines:", error);
        },
      });
  }

  onActionClicked(event: {
    action: GridAction;
    item: Turbine;
    event: Event;
  }): void {
    if (event.action.label === "Edit") {
      this.onEditTurbine(event.item);
    }
  }

  onEditTurbine(turbine: Turbine): void {
    this.router.navigate(["/turbines/edit", turbine.id]);
  }

  onAddTurbine(): void {
    this.router.navigate(["/turbines/create"]);
  }

  onBulkUpload(): void {
    const dialogRef = this.dialog.open(TurbineBulkUploadComponent, {
      width: '600px',
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // Refresh the turbine list after successful upload
        this.loadTurbines();
      }
    });
  }

  onStatusFilterChange(): void {
    this.loadTurbines();
  }

  // Helper methods for accessing nested hierarchy data
  getBusinessUnitName(turbine: Turbine): string {
    return turbine.plant?.assetGroup?.businessUnit?.name || "N/A";
  }

  getAssetGroupName(turbine: Turbine): string {
    return turbine.plant?.assetGroup?.name || "N/A";
  }
}
